import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@helios/auth-firebase-web';
import { RightDrawer, useBrandTokens } from '../../../shell/shared';
import { PERM } from '../../../auth/permissionMap';
import { isInvoiceMirrorNote, noteBodyChanged } from '@helios/data-access';
import { savePatientNoteBody, deletePatientNote } from '../../../services/patient-note-service';

/**
 * Edit or delete one patient note.
 *
 * Deliberately a plain textarea. Notes reach the Treatment Record from the quick
 * toolbar, which only ever collects free text — so the editor that fixes them collects
 * free text too. Type, visibility and alert level belong to the full composer in the
 * Visit Note Notes tab and are left alone here rather than half-exposed.
 *
 * A note mirrored from an invoice is editable, but says so: the change writes through
 * to the invoice (see `patient-note-service`), and the user should know that before
 * they touch it.
 *
 * PERMISSION — two levels, because the two actions are not equally recoverable.
 *
 *   EDIT   `Patient Module | Patient Workspace` at `edit`. Correcting a note keeps the
 *          record, and the person who most needs to fix a typo is its author, who holds
 *          `edit`. Requiring `full` here would leave a doctor unable to amend their own
 *          note on tenants where they are granted `edit`.
 *   DELETE the same sub-module at `full` — the level Permanent Delete Patient uses.
 *          `patient_notes` has no `deleted_at`, so removal is immediate, irreversible
 *          and propagates to every device. That is not the same act as rewording.
 *
 * Reaching the Treatment Record needs only `view`, so both gates genuinely restrict
 * someone. Without `edit` the drawer still opens read-only — seeing a note in full is
 * useful and harmless, since the Notes column truncates it.
 */

/** Minimal shape of the PowerSync handle this drawer forwards to the service. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface EditableNote {
  id: string;
  title: string | null;
  body: string | null;
  author_name: string | null;
  created_at: string | null;
}

export interface PatientNoteEditorDrawerProps {
  /**
   * The notes this drawer can act on — the whole day's notes when opened from the
   * row's Open button, or a single note when opened from its bullet. Null or empty
   * closes the drawer.
   *
   * Pass a LIVE value (derived from the query), not a snapshot: deleting a note has to
   * remove it from the list without reopening the drawer.
   */
  notes: EditableNote[] | null;
  /** Jump straight to this note. Absent + several notes shows the picker first. */
  initialNoteId?: string | null;
  onClose: () => void;
  db: Db | null;
}

function formatStamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function PatientNoteEditorDrawer({
  notes,
  initialNoteId = null,
  onClose,
  db,
}: PatientNoteEditorDrawerProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { canAccess } = usePermissions();
  const canEdit = canAccess(PERM.patientWorkspaceEdit);
  const canDelete = canAccess(PERM.patientWorkspaceFull);
  /**
   * Three states, and the distinction matters:
   *   undefined — untouched, so `initialNoteId` (or the sole note) decides;
   *   null      — the user deliberately went back to the picker;
   *   string    — the user picked a note.
   *
   * Collapsing null and undefined into one would make "back to all notes" fall
   * through to `initialNoteId` and bounce straight back into the note it just left.
   */
  const [selectedId, setSelectedId] = useState<string | null | undefined>(undefined);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState<'save' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const list = useMemo(() => notes ?? [], [notes]);
  const open = list.length > 0;
  // A single note needs no picker — go straight to editing it, which is also what the
  // row Open button does on a day with one note.
  const soleNoteId = list.length === 1 ? list[0].id : null;
  const note = useMemo(() => {
    const targetId = selectedId !== undefined ? selectedId : (initialNoteId ?? soleNoteId);
    return targetId ? (list.find((n) => n.id === targetId) ?? null) : null;
  }, [list, selectedId, initialNoteId, soleNoteId]);
  // Only offer "back" when there is actually a list to go back to.
  const showBackToList = list.length > 1 && !!note;

  // Reset whenever a different note comes into focus — otherwise the previous note's
  // text (or a half-typed edit) would leak into the next one.
  useEffect(() => {
    setBody(note?.body ?? '');
    setError(null);
    setBusy(null);
    setConfirmingDelete(false);
  }, [note?.id, note?.body]);

  // Hand control back to `initialNoteId` whenever the drawer is closed, or reopened on
  // a different note — a stale selection would otherwise win over the new target.
  useEffect(() => {
    setSelectedId(undefined);
  }, [open, initialNoteId]);

  const isMirror = useMemo(() => isInvoiceMirrorNote(note?.id), [note?.id]);
  const dirty = note ? noteBodyChanged(note.body, body) : false;
  const canSave = canEdit && !!db && !!note && dirty && body.trim().length > 0 && busy === null;

  const handleSave = useCallback(async () => {
    // Checked here as well as on the button: a disabled control is a hint, not a gate.
    if (!canEdit || !db || !note || busy) return;
    setBusy('save');
    setError(null);
    const result = await savePatientNoteBody({ db, noteId: note.id, body });
    if (!result.ok) {
      setBusy(null);
      setError(result.error ?? 'Could not save the note.');
      return;
    }
    setBusy(null);
    if (list.length > 1) setSelectedId(null);
    else onClose();
  }, [canEdit, db, note, body, busy, list.length, onClose]);

  const handleDelete = useCallback(async () => {
    if (!canDelete || !db || !note || busy) return;
    if (!confirmingDelete) {
      // One deliberate second step. A note is clinical narrative and there is no undo.
      setConfirmingDelete(true);
      return;
    }
    setBusy('delete');
    setError(null);
    const result = await deletePatientNote({ db, noteId: note.id });
    if (!result.ok) {
      setBusy(null);
      setConfirmingDelete(false);
      setError(result.error ?? 'Could not delete the note.');
      return;
    }
    setBusy(null);
    setConfirmingDelete(false);
    // With other notes still on this day, drop back to the picker so a second one can
    // be dealt with straight away. The live list has already lost the deleted note.
    if (list.length > 1) setSelectedId(null);
    else onClose();
  }, [canDelete, db, note, busy, confirmingDelete, list.length, onClose]);

  const stamp = formatStamp(note?.created_at ?? null);
  const title = note?.title?.trim();

  return (
    <RightDrawer
      open={open}
      onClose={() => {
        if (busy) return; // no dismissing mid-write
        onClose();
      }}
      title={list.length > 1 && !note ? 'Notes' : 'Note'}
      icon="edit"
      contentPadding="var(--sp-5)"
    >
      {/* Picker — only when the day has several notes and none is chosen yet. */}
      {open && !note && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <p
            style={{
              margin: '0 0 var(--sp-2)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
            }}
          >
            Choose a note to edit or delete.
          </p>
          {list.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => setSelectedId(n.id)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                display: 'block',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
                lineHeight: 'var(--line-height-sm)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <span style={{ display: 'block' }}>
                {n.title?.trim() ? `${n.title.trim()} — ` : ''}
                {n.body?.trim() || '(empty note)'}
              </span>
              {formatStamp(n.created_at) && (
                <span
                  style={{
                    display: 'block',
                    marginTop: 2,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {[formatStamp(n.created_at), n.author_name].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {note && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {showBackToList && (
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              disabled={busy !== null}
              style={{
                all: 'unset',
                alignSelf: 'flex-start',
                cursor: busy ? 'not-allowed' : 'pointer',
                fontSize: 'var(--font-size-xs)',
                color: primary,
                fontWeight: 'var(--font-weight-semi)',
              }}
            >
              ← All notes for this day
            </button>
          )}
          {(title || stamp || note.author_name) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {title && (
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  {title}
                </span>
              )}
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {[stamp, note.author_name].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}

          {isMirror && (
            <p
              style={{
                margin: 0,
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--surface-muted)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                lineHeight: 'var(--line-height-sm)',
              }}
            >
              This note came from an invoice. Saving or deleting it here updates the invoice&rsquo;s
              notes too.
            </p>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              {canEdit ? 'Note' : 'Note (view only)'}
            </span>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setConfirmingDelete(false);
              }}
              readOnly={!canEdit}
              disabled={busy !== null}
              rows={10}
              autoFocus
              aria-label="Note text"
              style={{
                width: '100%',
                resize: 'vertical',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: busy || !canEdit ? 'var(--surface-muted)' : 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                lineHeight: 'var(--line-height-sm)',
                fontFamily: 'var(--font-family)',
              }}
            />
          </label>

          {error && (
            <p
              role="alert"
              style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)' }}
            >
              {error}
            </p>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3)',
            }}
          >
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy !== null}
                style={{
                  height: 34,
                  padding: '0 var(--sp-4)',
                  borderRadius: 'var(--radius-md)',
                  border: `1px solid var(--color-danger)`,
                  background: confirmingDelete ? 'var(--color-danger)' : 'transparent',
                  color: confirmingDelete ? '#fff' : 'var(--color-danger)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {busy === 'delete'
                  ? 'Deleting…'
                  : confirmingDelete
                    ? 'Tap again to delete'
                    : 'Delete'}
              </button>
            )}
            {!canDelete && <span />}

            <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={busy !== null}
                style={{
                  height: 34,
                  padding: '0 var(--sp-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                {canEdit ? 'Cancel' : 'Close'}
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!canSave}
                  style={{
                    height: 34,
                    padding: '0 var(--sp-4)',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: canSave ? primary : 'var(--surface-muted)',
                    color: canSave ? '#fff' : 'var(--text-placeholder)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semi)',
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {busy === 'save' ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </RightDrawer>
  );
}
